import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MainPageWrapper from "../../../../../../components/MainPageWrapper/MainPageWrapper";
import { createCampaignBatchesColumns } from "../CampaignBatchColumns/CampaignBatchColumns";
import { useDownloadBatchProspectsCsv } from "../../../BatchProspectManagement/hooks/useDownloadBatchProspectsCsv";
import { useCancelBatch } from "@/modules/stochastic/features/CampaignBatchManagement/hooks/useCancelBatch";
import CampaignBatchListFilters from "@/modules/stochastic/features/CampaignBatchManagement/components/CampaignBatchListFilter/CampaignBatchListFilters";
import CancelBatchModal from "@/modules/stochastic/features/CampaignBatchManagement/components/CancelBatchModal/CancelBatchModal";
import { CampaignStatus } from "@/modules/stochastic/features/CampaignManagement/api/fetchCampaignStatuses/types";
import { fetchBatchStatuses } from "@/api/fetchBatchStatuses/fetchBatchStatuses";
import { useCampaignBatches } from "@/modules/stochastic/features/CampaignBatchManagement/hooks/useCampaignBatches";
import { Batch } from "@/api/getCampaignBatches/types";
import DataTable from "@/components/Datatable/Datatable";
import { useCampaignInfo } from "@/modules/stochastic/features/shared/hooks/useCampaignInfo";

const CampaignBatchList: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const numericCampaignId = Number(campaignId);

  const {
    campaign,
    loading: loadingCampaign,
    error: errorCampaign,
  } = useCampaignInfo(numericCampaignId);

  const [showCancelBatchModal, setShowCancelBatchModal] = useState(false);
  const [isCancelingBatch, setIsCancelingBatch] = useState(false);
  const [batchIdToCancel, setBatchIdToCancel] = useState<number | null>(null);

  const {
    batches,
    totalCount,
    loading,
    error,
    pagination,
    handlePaginationChange,
    sorting,
    handleBatchStatusChange,
    batchStatusId,
    fetchBatches,
    handleSortingChange,
  } = useCampaignBatches(numericCampaignId);

  const [batchStatuses, setBatchStatuses] = useState<CampaignStatus[]>([]);

  const {
    downloadCsv,
    loading: csvLoading,
    error: csvError,
  } = useDownloadBatchProspectsCsv();

  const {
    cancelBatch,
    loading: cancelBatchLoading,
    error: cancelBatchError,
  } = useCancelBatch();

  const handleViewProspects = useCallback(
    (batchId: number) => {
      navigate(
        `/lead-generation/campaigns/${campaignId}/batches/${batchId}/prospects`,
      );
    },
    [navigate, campaignId],
  );

  const handleDownloadCsv = useCallback(
    (batchId: number) => {
      downloadCsv(batchId);
    },
    [downloadCsv],
  );

  const handleOpenCancelBatchModal = useCallback((batchId: number) => {
    setShowCancelBatchModal(true);
    setBatchIdToCancel(batchId);
  }, []);

  const handleCloseCancelBatchModal = () => {
    setShowCancelBatchModal(false);
    setBatchIdToCancel(null);
  };

  const handleCancelBatchClick = async () => {
    if (batchIdToCancel === null) {
      console.error("No batch ID selected for canceling.");
      return;
    }
    setIsCancelingBatch(true);
    try {
      await cancelBatch(batchIdToCancel);
      handleCloseCancelBatchModal();
      fetchBatches();
    } catch (error) {
      console.error("Failed to cancel batch:", error);
    } finally {
      setIsCancelingBatch(false);
    }
  };

  const columns = useMemo(
    () =>
      createCampaignBatchesColumns({
        handleViewProspects,
        handleDownloadCsv,
        handleOpenCancelBatchModal,
      }),
    [handleViewProspects, handleDownloadCsv, handleOpenCancelBatchModal],
  );

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const response = await fetchBatchStatuses();
        setBatchStatuses(response.data);
      } catch (err) {
        console.error("Failed to fetch batch statuses", err);
      }
    };
    loadStatuses();
  }, []);

  const handleFilterChange = useCallback(
    (newStatusId?: number) => {
      handleBatchStatusChange(newStatusId);
    },
    [handleBatchStatusChange],
  );

  const combinedError = error || csvError || cancelBatchError || errorCampaign;

  const combinedLoading =
    loading || csvLoading || cancelBatchLoading || loadingCampaign;

  return (
    <MainPageWrapper error={combinedError} title="Campaign Batches">
      <CampaignBatchListFilters
        batchStatusId={batchStatusId}
        batchStatuses={batchStatuses}
        onFilterChange={handleFilterChange}
      />

      <DataTable<Batch>
        columns={columns}
        data={batches}
        error={combinedError}
        loading={combinedLoading}
        noDataMessage="No batches found"
        onPageChange={(newPageIndex, newPageSize) =>
          handlePaginationChange({
            pageIndex: newPageIndex,
            pageSize: newPageSize,
          })
        }
        onSortingChange={handleSortingChange}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        rowKeyExtractor={(item) => item.id}
        sorting={sorting}
        totalCount={totalCount}
      />

      <CancelBatchModal
        isCanceling={isCancelingBatch}
        isOpen={showCancelBatchModal}
        onClose={handleCloseCancelBatchModal}
        onConfirm={handleCancelBatchClick}
      />
    </MainPageWrapper>
  );
};

export default CampaignBatchList;
